import React, { createContext, useContext, useState, useRef, useCallback, useMemo } from 'react';
import { fetchCart, saveCart } from './cartApi';
import { fetchProducts } from './api';
import { fetchOffers } from './offersApi';
import { fetchBranches, fetchBranchConnections } from './branchesApi';
import { clearCurrencyOnLogout } from './currencyHelpers';

const CartContext = createContext();

export function useCart() {
  return useContext(CartContext);
}

export function useUser() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useUser must be used within a CartProvider');
  }
  return {
    user: context.user,
    setUser: context.setUser,
    logout: context.logout,
  };
}

export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

// ────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────

const normalizeBranchId = (id) => {
  if (id === null || id === undefined || id === '') return '';
  return String(id);
};

const getBranchId = (branch) => {
  if (!branch) return null;
  return branch.Id ?? branch.id ?? null;
};

const getBranchName = (branch) =>
  branch?.Name || branch?.name || 'Branch';

const getItemBranchId = (item) => {
  const raw =
    item?.BranchId ??
    item?.branchId ??
    item?.Branch_Id ??
    item?.branch_id ??
    null;
  if (raw === null || raw === undefined || raw === '') return null;
  return String(raw);
};

const buildCartKey = (item, branchIdOverride) => {
  const pid = item.ProductId || item.Id || item.id;
  const uid = item.UniqueId || '';
  const color = item.SelectedColor || '';
  const size = item.SelectedSize || '';
  const bid = normalizeBranchId(
    branchIdOverride !== undefined ? branchIdOverride : item.branchId
  );
  return `${pid}_${uid}_${color}_${size}_${bid}`;
};

const buildCartKeyNoBranch = (item) => {
  const pid = item.ProductId || item.Id || item.id;
  const uid = item.UniqueId || '';
  const color = item.SelectedColor || '';
  const size = item.SelectedSize || '';
  return `${pid}_${uid}_${color}_${size}`;
};

const isOfferProduct = (product) => {
  const categoryName = product.categoryName || product.CategoryName || '';
  return (
    categoryName.includes('العروض') ||
    categoryName.includes('Offer') ||
    categoryName.includes('Deal') ||
    !!product.OfferCategoryId ||
    !!product.offerCategoryId
  );
};

const hasSubProducts = (item) =>
  Array.isArray(item?.SubProducts) && item.SubProducts.length > 0;

/**
 * Resolves CPAccountActive from any backend casing/type.
 * Picks the FIRST source that has a defined value (skips undefined/null).
 */
const resolveCpActive = (...sources) => {
  for (const src of sources) {
    if (src === undefined || src === null) continue;
    const raw =
      src?.CPAccountActive ??
      src?.cpAccountActive ??
      src?.CPAccountActiveValue ??
      src?.cp_account_active;
    if (raw === undefined || raw === null) continue;
    return (
      raw === true ||
      raw === 1 ||
      raw === '1' ||
      String(raw).toLowerCase() === 'true'
    );
  }
  return false;
};

/**
 * Enrich raw cart items with product data, for ONE branch.
 * Strictly filters out items that don't belong to this branch.
 */
function enrichCartItems(items, products, offerProducts, branch) {
  const allOfferProducts = (offerProducts || []).flatMap((offer) =>
    (offer.products || offer.Products || []).map((prod) => {
      const productId = prod.id || prod.Id;
      const offerCategoryId = offer.offerCategoryId || offer.Id;
      return {
        ProductId: productId,
        Id: productId,
        ProductName: prod.productName || prod.ProductName,
        Price: prod.price || prod.Price,
        OldPrice: prod.oldPrice || prod.OldPrice,
        Currency: prod.currency || prod.Currency,
        CurrencyPrices: prod.currencyPrices || prod.CurrencyPrices,
        OldCurrencyPrices: prod.oldCurrencyPrices || prod.OldCurrencyPrices,
        ImageUrl: prod.imageUrl || prod.ImageUrl || prod.imageJson || prod.ImageJson,
        ImageJson: prod.imageJson || prod.ImageJson || prod.imageUrl || prod.ImageUrl,
        Description: prod.description || prod.Description,
        categoryName: offer.categoryName || offer.CategoryName,
        CategoryName: offer.categoryName || offer.CategoryName,
        OfferCategoryId: offerCategoryId,
        offerCategoryId: offerCategoryId,
        UniqueId: '1',
        ...prod,
      };
    })
  );

  const allProducts = [...(products || []), ...allOfferProducts];

  const branchId = getBranchId(branch);
  const branchIdStr = normalizeBranchId(branchId);
  const branchName = getBranchName(branch);

  const map = new Map();

  for (const rawItem of items || []) {
    // ── STRICT BRANCH FILTER ─────────────────────────────────
    const itemBid = getItemBranchId(rawItem);
    if (branchIdStr === '') {
      if (itemBid !== null) continue;
    } else {
      if (itemBid !== branchIdStr) continue;
    }
    // ─────────────────────────────────────────────────────────

    const productId = rawItem.ProductId || rawItem.id || rawItem.Id;
    const color = rawItem.SelectedColor || '';
    const size = rawItem.SelectedSize || '';
    const uid = rawItem.UniqueId || '';
    const key = `${productId}_${uid}_${color}_${size}_${branchIdStr}`;

    if (map.has(key)) {
      map.get(key).quantity += Number(rawItem.Quantity || rawItem.quantity || 1);
      continue;
    }

    const prod = allProducts.find(
      (p) => p.ProductId === productId || p.Id === productId
    );

    const subProducts = Array.isArray(rawItem.SubProducts) ? rawItem.SubProducts : [];

    if (prod || subProducts.length > 0) {
      // ✅ CRITICAL: Cart row takes PRIORITY over the live product.
      //    The cart row captured the CP value AT THE TIME OF ADDING.
      //    Only fall back to the live product if the cart row has nothing.
      const cpActive = resolveCpActive(rawItem, prod);

      map.set(key, {
        ...(prod || {}),
        ...rawItem,
        Id: productId,
        ProductId: productId,
        UniqueId: rawItem.UniqueId || prod?.UniqueId || null,
        SelectedColor: rawItem.SelectedColor || null,
        SelectedSize: rawItem.SelectedSize || null,
        quantity: Number(rawItem.Quantity || rawItem.quantity || 1),

        // Pricing fields
        Price: rawItem.Price ?? prod?.Price,
        OldPrice: rawItem.OldPrice ?? prod?.OldPrice,
        CurrencyPrices: rawItem.CurrencyPrices ?? prod?.CurrencyPrices,
        OldCurrencyPrices:
          rawItem.OldCurrencyPrices ?? prod?.OldCurrencyPrices,

        // Custom-bundle fields
        SubProducts: subProducts,
        isCustom: subProducts.length > 0 ? true : !!rawItem.isCustom,
        CustomTotal: rawItem.CustomTotal ?? prod?.CustomTotal,

        // ✅ CP flag in all 3 casings cart/checkout might read
        CPAccountActive: cpActive,
        cpAccountActive: cpActive,
        CPAccountActiveValue: cpActive ? 1 : 0,

        branchId: branchId ?? undefined,
        branchName,
      });
    }
  }

  return Array.from(map.values());
}

function flattenBranchGroups(groups) {
  return groups.flatMap((g) => g.items);
}

// ────────────────────────────────────────────────────────────────────────
// Provider
// ────────────────────────────────────────────────────────────────────────

export function CartProvider({ children }) {
  const getUserId = () => {
    try {
      const u = JSON.parse(localStorage.getItem('user'));
      return u?.Id || u?.id || null;
    } catch {
      return null;
    }
  };

  const [user, setUserState] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [selectedBranch, setSelectedBranch] = useState(() => {
    try {
      const saved = localStorage.getItem('selectedBranch');
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error('Failed to parse saved branch:', e);
      localStorage.removeItem('selectedBranch');
    }
    return null;
  });

  const [isLocatingBranch, setIsLocatingBranch] = useState(true);

  const [cartItems, setCartItems] = useState([]);
  const [branchCarts, setBranchCarts] = useState([]);
  const [loadingCart, setLoadingCart] = useState(false);

  const branchCartsRef = useRef([]);
  const selectedBranchRef = useRef(selectedBranch);
  const userRef = useRef(user);
  const isSavingRef = useRef(false);
  const saveVersionRef = useRef(0);

  React.useEffect(() => {
    branchCartsRef.current = branchCarts;
  }, [branchCarts]);

  React.useEffect(() => {
    selectedBranchRef.current = selectedBranch;
  }, [selectedBranch]);

  React.useEffect(() => {
    userRef.current = user;
  }, [user]);

  // ── Auto-detect nearest branch on first load ─────────────────────
  React.useEffect(() => {
    const detectNearestBranch = async () => {
      const storedBranch = (() => {
        try {
          const saved = localStorage.getItem('selectedBranch');
          return saved ? JSON.parse(saved) : null;
        } catch {
          return null;
        }
      })();

      if (storedBranch) {
        setSelectedBranch(storedBranch);
        setIsLocatingBranch(false);
        return;
      }

      setIsLocatingBranch(true);
      try {
        const branches = await fetchBranches();
        if (!branches || branches.length === 0) {
          setIsLocatingBranch(false);
          return;
        }

        if (!navigator.geolocation) {
          const firstActive = branches.find((b) => b.isActive) || branches[0];
          setSelectedBranch(firstActive);
          localStorage.setItem('selectedBranch', JSON.stringify(firstActive));
          setIsLocatingBranch(false);
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            let nearestBranch = null;
            let minDistance = Infinity;

            branches.forEach((branch) => {
              if (branch.latitude && branch.longitude && branch.isActive) {
                const dist = calculateDistance(
                  latitude,
                  longitude,
                  branch.latitude,
                  branch.longitude
                );
                if (dist < minDistance) {
                  minDistance = dist;
                  nearestBranch = branch;
                }
              }
            });

            const finalBranch =
              nearestBranch || branches.find((b) => b.isActive) || branches[0];
            setSelectedBranch(finalBranch);
            localStorage.setItem('selectedBranch', JSON.stringify(finalBranch));
            setIsLocatingBranch(false);
          },
          () => {
            const firstActive = branches.find((b) => b.isActive) || branches[0];
            setSelectedBranch(firstActive);
            localStorage.setItem('selectedBranch', JSON.stringify(firstActive));
            setIsLocatingBranch(false);
          },
          { enableHighAccuracy: true }
        );
      } catch (error) {
        console.error('Error detecting nearest branch:', error);
        setIsLocatingBranch(false);
      }
    };

    detectNearestBranch();
  }, []);

  const updateSelectedBranch = useCallback((branch) => {
    setCartItems([]);
    setBranchCarts([]);
    branchCartsRef.current = [];
    saveVersionRef.current += 1;

    setSelectedBranch(branch);
    if (branch) {
      localStorage.setItem('selectedBranch', JSON.stringify(branch));
    } else {
      localStorage.removeItem('selectedBranch');
    }
  }, []);

  const setUser = useCallback((newUser) => {
    setUserState(newUser);
    if (newUser) {
      localStorage.setItem('user', JSON.stringify(newUser));
    } else {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    }
    window.dispatchEvent(new Event('userChanged'));
  }, []);

  const logout = useCallback(async () => {
    setUser(null);
    setCartItems([]);
    setBranchCarts([]);
    branchCartsRef.current = [];
    saveVersionRef.current += 1;
    clearCurrencyOnLogout();
  }, [setUser]);

  const refreshAllBranchCarts = useCallback(async () => {
    if (isSavingRef.current) return;

    const userId = getUserId();
    const activeBranch = selectedBranchRef.current;

    if (!userId) {
      setCartItems([]);
      setBranchCarts([]);
      branchCartsRef.current = [];
      return;
    }

    setLoadingCart(true);
    try {
      const currentVersion = saveVersionRef.current;

      if (!activeBranch) {
        const [products, offers, items] = await Promise.all([
          fetchProducts(undefined),
          fetchOffers(undefined),
          fetchCart(userId, null),
        ]);

        if (currentVersion !== saveVersionRef.current) return;

        const enriched = enrichCartItems(
          items || [],
          products ?? [],
          offers ?? [],
          null
        );

        setCartItems(enriched);
        setBranchCarts([]);
        branchCartsRef.current = [];
        return;
      }

      const activeBranchId = getBranchId(activeBranch);
      const activeBranchIdStr = String(activeBranchId ?? '');

      const connectionsRaw = await fetchBranchConnections(activeBranchId).catch(
        () => []
      );

      const connectedBranches = Array.isArray(connectionsRaw)
        ? connectionsRaw.filter((b) => {
            if (!b) return false;
            const bid = String(getBranchId(b) ?? '');
            return bid && bid !== activeBranchIdStr;
          })
        : [];

      const uniqueBranchesMap = new Map();
      uniqueBranchesMap.set(activeBranchIdStr, activeBranch);
      for (const b of connectedBranches) {
        const bid = String(getBranchId(b) ?? '');
        if (bid && !uniqueBranchesMap.has(bid)) uniqueBranchesMap.set(bid, b);
      }

      const allowedBranchIds = new Set(uniqueBranchesMap.keys());
      const allBranches = Array.from(uniqueBranchesMap.values());

      const groups = await Promise.all(
        allBranches.map(async (branch) => {
          const branchId = getBranchId(branch);
          const [products, offers, items] = await Promise.all([
            fetchProducts(branchId),
            fetchOffers(branchId),
            fetchCart(userId, branchId),
          ]);
          return {
            branch,
            items: enrichCartItems(
              items || [],
              products ?? [],
              offers ?? [],
              branch
            ),
          };
        })
      );

      if (currentVersion !== saveVersionRef.current) return;

      const filtered = groups.filter((g) =>
        allowedBranchIds.has(String(getBranchId(g.branch) ?? ''))
      );
      const nonEmpty = filtered.filter((g) => g.items.length > 0);

      setBranchCarts(nonEmpty);
      branchCartsRef.current = nonEmpty;
      setCartItems(flattenBranchGroups(nonEmpty));
    } catch (err) {
      console.error('CartContext: error fetching carts', err);
      setCartItems([]);
      setBranchCarts([]);
      branchCartsRef.current = [];
    } finally {
      setLoadingCart(false);
    }
  }, []);

  React.useEffect(() => {
    refreshAllBranchCarts();
  }, [refreshAllBranchCarts, selectedBranch, user]);

  React.useEffect(() => {
    const handler = () => refreshAllBranchCarts();
    window.addEventListener('userChanged', handler);
    return () => window.removeEventListener('userChanged', handler);
  }, [refreshAllBranchCarts]);

  const saveCartToBackend = useCallback(
    async (newGroups) => {
      const userId = getUserId();
      const activeBranch = selectedBranchRef.current;
      const activeBranchId = getBranchId(activeBranch);

      if (!userId) return;

      const previousGroups = branchCartsRef.current;

      isSavingRef.current = true;
      saveVersionRef.current += 1;

      setBranchCarts(newGroups);
      branchCartsRef.current = newGroups;
      setCartItems(flattenBranchGroups(newGroups));

      const buildPayload = (item, branchIdForItem) => {
        const productId = Number(item.ProductId || item.Id || item.id);
        const uniqueId = isOfferProduct(item) ? '1' : item.UniqueId || null;

        const cpActive = resolveCpActive(item);

        const payload = {
          ProductId: productId,
          Quantity: Number(item.Quantity || item.quantity || 1),
          UniqueId: uniqueId,
          SelectedColor: item.SelectedColor || null,
          SelectedSize: item.SelectedSize || null,
          Price: item.Price ?? item.price ?? null,
          Currency: item.Currency || item.currency || 'YER',
          CurrencyPrices: item.CurrencyPrices || item.currencyPrices || null,
          ImageJson:
            item.ImageJson || item.imageJson || item.ImageUrl || item.imageUrl,
          ImageUrl:
            item.ImageUrl || item.imageUrl || item.ImageJson || item.imageJson,
          ProductName: item.ProductName || item.name || null,
          Description: item.Description || item.description || null,
          BranchId:
            branchIdForItem !== undefined && branchIdForItem !== null
              ? branchIdForItem
              : null,
          // ✅ Persist CP flag so it survives a backend round-trip.
          CPAccountActive: cpActive,
          CPAccountActiveValue: cpActive ? 1 : 0,
        };

        if (hasSubProducts(item)) {
          payload.SubProducts = item.SubProducts;
          payload.isCustom = true;
          payload.IsCustom = true;
        } else if (item.isCustom && item.CustomTotal != null) {
          payload.isCustom = true;
          payload.IsCustom = true;
          payload.CustomTotal = item.CustomTotal;
        }

        return payload;
      };

      try {
        const newBranchIds = new Set(
          newGroups.map((g) => String(getBranchId(g.branch) ?? ''))
        );
        const removedBranchIds = previousGroups
          .map((g) => getBranchId(g.branch))
          .filter(
            (bid) =>
              bid !== null &&
              bid !== undefined &&
              !newBranchIds.has(String(bid))
          );

        if (newGroups.length === 0) {
          const branchesToClear = new Set();
          if (activeBranchId !== null && activeBranchId !== undefined) {
            branchesToClear.add(String(activeBranchId));
          }
          for (const bid of removedBranchIds) {
            if (bid !== null && bid !== undefined) {
              branchesToClear.add(String(bid));
            }
          }

          if (branchesToClear.size === 0) {
            await saveCart(userId, [], activeBranchId);
          } else {
            await Promise.all(
              Array.from(branchesToClear).map((bid) =>
                saveCart(userId, [], bid)
              )
            );
          }
        } else {
          await Promise.all(
            newGroups.map(async (group) => {
              const groupBranchId = getBranchId(group.branch);
              const payload = group.items
                .map((it) => buildPayload(it, groupBranchId))
                .filter(
                  (i) =>
                    Number.isFinite(i.ProductId) && Number.isFinite(i.Quantity)
                );
              await saveCart(userId, payload, groupBranchId);
            })
          );

          if (removedBranchIds.length > 0) {
            await Promise.all(
              removedBranchIds.map((bid) => saveCart(userId, [], bid))
            );
          }
        }
      } catch (err) {
        console.error('saveCart failed:', err);
      } finally {
        isSavingRef.current = false;
        await refreshAllBranchCarts();
      }
    },
    [refreshAllBranchCarts]
  );

  const mutateGroups = useCallback((targetBranchId, mutator) => {
    const current = branchCartsRef.current;
    const targetBidStr = normalizeBranchId(targetBranchId);

    if (current.length === 0) {
      const activeBranch = selectedBranchRef.current;
      const activeBid = normalizeBranchId(getBranchId(activeBranch));

      if (targetBidStr === '' || targetBidStr === activeBid) {
        const seedItems = mutator([]);
        if (seedItems.length === 0) return [];
        return [
          {
            branch: activeBranch || { Id: null, Name: 'Cart' },
            items: seedItems,
          },
        ];
      }
      return current;
    }

    let matched = false;
    const next = current.map((g) => {
      const gid = normalizeBranchId(getBranchId(g.branch));
      if (gid === targetBidStr) {
        matched = true;
        return { ...g, items: mutator(g.items) };
      }
      return g;
    });

    if (!matched) {
      const seedItems = mutator([]);
      if (seedItems.length > 0) {
        const activeBranch = selectedBranchRef.current;
        next.push({
          branch: activeBranch || { Id: targetBranchId, Name: 'Branch' },
          items: seedItems,
        });
      }
    }

    return next.filter((g) => g.items.length > 0);
  }, []);

  // ── Find item by key with fallbacks ──────────────────────────────
  const findItemInList = (items, id, targetBranchId) => {
    const targetBidStr = normalizeBranchId(targetBranchId);

    let existing = items.find(
      (item) => buildCartKey(item, targetBranchId) === id
    );
    if (existing) return existing;

    existing = items.find((item) => buildCartKeyNoBranch(item) === id);
    if (existing) return existing;

    if (typeof id === 'string') {
      const suffix = `_${targetBidStr}`;
      let stripped = id;
      if (targetBidStr && id.endsWith(suffix)) {
        stripped = id.slice(0, -suffix.length);
      } else if (id.endsWith('_')) {
        stripped = id.slice(0, -1);
      }
      existing = items.find((item) => buildCartKeyNoBranch(item) === stripped);
      if (existing) return existing;

      const parts = stripped.split('_');
      if (parts.length === 4) {
        const [pid, uid, color, size] = parts;
        existing = items.find((item) => {
          const itemPid = String(item.ProductId || item.Id || item.id);
          const itemUid = item.UniqueId || '';
          const itemColor = item.SelectedColor || '';
          const itemSize = item.SelectedSize || '';
          return (
            itemPid === pid &&
            itemUid === uid &&
            itemColor === color &&
            itemSize === size
          );
        });
        if (existing) return existing;
      }
      if (parts.length === 3) {
        const [pid, uid, color] = parts;
        existing = items.find((item) => {
          const itemPid = String(item.ProductId || item.Id || item.id);
          const itemUid = item.UniqueId || '';
          const itemColor = item.SelectedColor || '';
          return (
            itemPid === pid &&
            itemUid === uid &&
            itemColor === color &&
            !item.SelectedSize
          );
        });
        if (existing) return existing;
      }
      if (parts.length === 2) {
        const [pid, color] = parts;
        existing = items.find((item) => {
          const itemPid = String(item.ProductId || item.Id || item.id);
          const itemColor = item.SelectedColor || '';
          const itemUid = item.UniqueId || '';
          return itemPid === pid && itemColor === color && !itemUid;
        });
        if (existing) return existing;
      }
    }

    const simpleId = String(id).split('_')[0];
    return items.find((item) => {
      const itemPid = String(item.ProductId || item.Id || item.id);
      return (
        itemPid === simpleId &&
        !item.SelectedColor &&
        !item.SelectedSize &&
        !item.UniqueId
      );
    });
  };

  // ── addToCart ────────────────────────────────────────────────────
  const addToCart = useCallback(
    (product, branchIdArg) => {
      const activeBranchId = getBranchId(selectedBranchRef.current);
      const targetBranchId =
        branchIdArg !== undefined && branchIdArg !== null
          ? branchIdArg
          : product.branchId !== undefined && product.branchId !== null
          ? product.branchId
          : activeBranchId;

      const productKey = buildCartKey(
        {
          ProductId: Number(product.ProductId || product.Id || product.id),
          UniqueId: isOfferProduct(product) ? '1' : product.UniqueId || null,
          SelectedColor: product.SelectedColor || '',
          SelectedSize: product.SelectedSize || '',
        },
        targetBranchId
      );

      const newGroups = mutateGroups(targetBranchId, (items) => {
        const idx = items.findIndex(
          (i) => buildCartKey(i, targetBranchId) === productKey
        );
        if (idx >= 0) {
          const updated = [...items];
          updated[idx] = {
            ...updated[idx],
            quantity: (updated[idx].quantity || 0) + 1,
          };
          return updated;
        }

        const productId = Number(product.ProductId || product.Id || product.id);
        const uniqueId = isOfferProduct(product) ? '1' : product.UniqueId || null;

        const cpActive = resolveCpActive(product);

        const subs = Array.isArray(product.SubProducts) ? product.SubProducts : [];

        const newItem = {
          ...product,
          Id: productId,
          ProductId: productId,
          UniqueId: uniqueId,
          SelectedColor: product.SelectedColor || null,
          SelectedSize: product.SelectedSize || null,
          quantity: 1,

          // Pricing fields preserved
          Price: product.Price ?? product.price ?? null,
          OldPrice: product.OldPrice ?? product.oldPrice ?? null,
          Currency: product.Currency || product.currency || 'YER',
          CurrencyPrices: product.CurrencyPrices || product.currencyPrices,
          OldCurrencyPrices:
            product.OldCurrencyPrices || product.oldCurrencyPrices,

          ImageUrl:
            product.ImageUrl ||
            product.imageUrl ||
            product.ImageJson ||
            product.imageJson,
          ImageJson:
            product.ImageJson ||
            product.imageJson ||
            product.ImageUrl ||
            product.imageUrl,
          ProductName: product.ProductName || product.name,
          Description: product.Description || product.description,

          // Custom-bundle fields
          SubProducts: subs,
          isCustom: subs.length > 0 ? true : !!product.isCustom,
          CustomTotal: product.CustomTotal ?? null,

          // ✅ CP flag in all 3 casings
          CPAccountActive: cpActive,
          cpAccountActive: cpActive,
          CPAccountActiveValue: cpActive ? 1 : 0,

          branchId: targetBranchId ?? undefined,
        };

        return [...items, newItem];
      });

      saveCartToBackend(newGroups);
    },
    [mutateGroups, saveCartToBackend]
  );

  // ── decrementFromCart ────────────────────────────────────────────
  const decrementFromCart = useCallback(
    (id, branchIdArg) => {
      const activeBranchId = getBranchId(selectedBranchRef.current);
      const targetBranchId =
        branchIdArg !== undefined && branchIdArg !== null
          ? branchIdArg
          : activeBranchId;

      const newGroups = mutateGroups(targetBranchId, (items) => {
        const existing = findItemInList(items, id, targetBranchId);
        if (!existing) return items;

        const existingKey = buildCartKey(existing, targetBranchId);
        if (existing.quantity <= 1) {
          return items.filter(
            (item) => buildCartKey(item, targetBranchId) !== existingKey
          );
        }
        return items.map((item) =>
          buildCartKey(item, targetBranchId) === existingKey
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
      });

      saveCartToBackend(newGroups);
    },
    [mutateGroups, saveCartToBackend]
  );

  // ── removeFromCart ───────────────────────────────────────────────
  const removeFromCart = useCallback(
    (id, branchIdArg) => {
      const activeBranchId = getBranchId(selectedBranchRef.current);
      const targetBranchId =
        branchIdArg !== undefined && branchIdArg !== null
          ? branchIdArg
          : activeBranchId;

      const newGroups = mutateGroups(targetBranchId, (items) => {
        const existing = findItemInList(items, id, targetBranchId);
        if (!existing) return items;
        const existingKey = buildCartKey(existing, targetBranchId);
        return items.filter(
          (item) => buildCartKey(item, targetBranchId) !== existingKey
        );
      });

      saveCartToBackend(newGroups);
    },
    [mutateGroups, saveCartToBackend]
  );

  // ── clearCart ────────────────────────────────────────────────────
  const clearCart = useCallback(() => {
    saveVersionRef.current += 1;
    setCartItems([]);
    setBranchCarts([]);
    branchCartsRef.current = [];
    saveCartToBackend([]);
  }, [saveCartToBackend]);

  // ── Context value ────────────────────────────────────────────────
  const value = useMemo(
    () => ({
      cartItems,
      branchCarts,
      loadingCart,
      refreshAllBranchCarts,
      addToCart,
      removeFromCart,
      decrementFromCart,
      clearCart,
      saveCartToBackend,
      selectedBranch,
      setSelectedBranch: updateSelectedBranch,
      isLocatingBranch,
      user,
      setUser,
      logout,
    }),
    [
      cartItems,
      branchCarts,
      loadingCart,
      refreshAllBranchCarts,
      addToCart,
      removeFromCart,
      decrementFromCart,
      clearCart,
      saveCartToBackend,
      selectedBranch,
      updateSelectedBranch,
      isLocatingBranch,
      user,
      setUser,
      logout,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}